(function () {
  'use strict';
  if (customElements.get('space-armory-cart')) return;
  const {CartStore, protectionVariant, checkoutReady, merchandise, isCoverage} = window.SpaceArmoryCartCore;
  // Text in the shopper's language: snippets/sa-i18n-js.liquid sets window.SA_I18N on French, Spanish and German
  // pages only. English pages have no dictionary, so every string falls back to its English text unchanged.
  const saText = (key, fallback, vars = null) => {
    let text = window.SA_I18N?.[key] ?? fallback;
    if (vars) for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, value);
    return text;
  };
  // Option names and values stay English in Shopify (the size, strap and colour logic reads them); other languages
  // show them through the dictionary.
  const optionName = name => window.SA_I18N?.[`option.${name}`] ?? name;
  const optionValue = value => window.SA_I18N?.[`option_value.${value}`] ?? value;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  const safeURL = value => {
    try { const url = new URL(value, location.origin); return ['https:', 'http:'].includes(url.protocol) ? escape(url.href) : ''; } catch { return ''; }
  };
  class SpaceArmoryCart extends HTMLElement {
    connectedCallback() {
      if (this.store) return;
      this.config = JSON.parse(this.querySelector('[data-sa-cart-config]').textContent);
      this.config.root = `${this.config.root.replace(/\/$/, '')}/`;
      this.config.rate = this.config.currency === this.config.baseCurrency ? 1 : Number(window.Shopify?.currency?.rate);
      // Shopify's t filter HTML-escapes ("l&#39;étape"); the drawer escapes again when it renders, so read the plain text.
      // English strings have no entities, so nothing changes there.
      const plain = value => { if (typeof value !== 'string' || !value.includes('&')) return value; const box = document.createElement('textarea'); box.innerHTML = value; return box.value; };
      this.strings = Object.fromEntries(Object.entries(this.config.strings || {}).map(([key, value]) => [key, plain(value)]));
      this.config.heading = plain(this.config.heading);
      this.config.upsellHeading = plain(this.config.upsellHeading);
      this.config.protectionText = plain(this.config.protectionText);
      this.recordRecentlyViewed();
      this.dialog = this.querySelector('#SaCartDialog');
      this.backdrop = this.querySelector('[data-sa-backdrop]');
      this.info = this.querySelector('#SaCheckoutInfo');
      this.deliveryInfo = this.querySelector('#SaDeliveryInfo');
      this.body = this.querySelector('[data-sa-body]');
      this.footer = this.querySelector('[data-sa-footer]');
      this.summary = this.querySelector('[data-sa-summary]');
      this.reward = this.querySelector('[data-sa-reward]');
      this.dock = this.querySelector('[data-sa-dock]');
      this.express = this.querySelector('[data-sa-express]');
      if (this.express) {
        this.walletMedia = window.matchMedia('(max-width: 749px)');
        this.walletMedia.addEventListener?.('change', () => this.syncExpressLayout());
        this.walletObserver = new MutationObserver(() => this.syncExpressLayout());
        this.walletObserver.observe(this.express, {childList: true, subtree: true});
        this.syncExpressLayout();
      }
      this.productCache = new Map((this.config.products || []).filter(Boolean).map(product => [product.handle, product]));
      this.productRequests = new Map();
      this.store = new CartStore({
        config: this.config,
        request: this.request.bind(this),
        onChange: cart => this.render(cart),
        onBusy: busy => this.setBusy(busy),
        onError: message => this.showError(message),
      });
      if (Array.isArray(this.config.initialCart?.items)) {
        this.store.cart = this.config.initialCart;
        this.store.confirmedAt = Date.now();
        this.render(this.store.cart);
      }
      this.addEventListener('pointerdown', () => { this.dataset.saKeyboard = 'false'; });
      this.addEventListener('keydown', event => { if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) this.dataset.saKeyboard = 'true'; });
      this.addEventListener('click', event => this.handleClick(event));
      for (const type of ['contextmenu', 'dragstart']) {
        this.addEventListener(type, event => {
          if (event.target.closest?.('.sa-cart-protection-mark')) event.preventDefault();
        });
      }
      window.SpaceArmoryFitAccount?.addEventListener('change', () => { if(this.store.cart && !this.store.pending)this.render(this.store.cart); });
      document.addEventListener('sa:visitor-country', () => { if (this.store.cart && !this.store.pending) this.render(this.store.cart); });
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-sa-variant]');
        const quantity = event.target.closest('[data-sa-quantity]');
        if (this.store.mutating || this.profileUpdating || (!select && !quantity)) return;
        this.clearError();
        if (quantity && (!quantity.value.trim() || !Number.isInteger(Number(quantity.value)) || Number(quantity.value) < 1)) {
          this.showError(this.strings.invalid_quantity);
          this.render(this.store.cart);
          return;
        }
        const original = select && this.store.cart?.items.find(item => item.key === select.dataset.key);
        const patch = original && String(original.variant_id) !== String(select.value)
          ? {'_Size Selection Method': 'Variant changed manually in cart'} : undefined;
        const action = select ? this.store.swapVariant(select.dataset.key, select.value, patch) : this.store.setQuantity(quantity.dataset.key, Number(quantity.value));
        action.then(() => this.broadcast()).catch(error => { this.showError(error.message); this.render(this.store.cart); });
      });
      for (const dialog of [this.dialog, this.info, this.deliveryInfo].filter(Boolean)) {
        dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeDialog(dialog); });
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog(dialog);
        });
      }
      this.dialog.addEventListener('keydown', event => {
        if (this.popupOpen()) return;
        if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
        if (event.key !== 'Tab') return;
        const elements = [...this.dialog.querySelectorAll('a[href],button,input,select,iframe,[tabindex]')].filter(el =>
          !el.disabled && el.tabIndex >= 0 && !el.closest('[hidden],[inert]') && el.getClientRects().length);
        const first = elements[0], last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
      document.addEventListener('click', event => {
        const link = event.target.closest('#cart-icon-bubble, [data-sa-open-cart]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(link);
      });
      document.addEventListener('keydown', event => {
        if (event.code === 'Space' && event.target.matches('#cart-icon-bubble')) { event.preventDefault(); this.open(event.target); }
        // Focus can still end up on the page (a tap outside the panel's controls); Escape closes the open drawer anyway.
        if (event.key === 'Escape' && this.dialog.open && !this.popupOpen() && (event.target === document.body || event.target === document.documentElement)) { event.preventDefault(); this.close(); }
      });
      const link = document.querySelector('#cart-icon-bubble');
      link?.setAttribute('aria-haspopup', 'dialog');
      link?.setAttribute('aria-controls', 'SaCartDialog');
      this.unsubscribe = typeof subscribe === 'function' && subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source === 'space-armory-cart' || event.source === 'product-form') return;
        this.store.refresh().catch(() => {});
      });
      window.addEventListener('pageshow', event => { this.navigating = false; this.setBusy(this.store.pending > 0); if (event.persisted) this.store.refresh().catch(() => {}); });
      document.addEventListener('visibilitychange', () => { if (!document.hidden && this.dialog.open) this.store.refresh({maxAge:15000}).catch(() => {}); });
      window.addEventListener('resize', () => this.updatePreviewOffset());
      this.updatePreviewOffset();
      // /cart sends shoppers here with #sa-cart (snippets/sa-cart-redirect.liquid): open once, drop the hash so a reload doesn't reopen it.
      if (location.hash === '#sa-cart') {
        history.replaceState(history.state, '', location.pathname + location.search);
        // Focus still goes to X, but without a tap first Chrome would draw its focus ring; the ring returns on the first key.
        this.dataset.saAutoFocus = 'true';
        this.addEventListener('keydown', () => delete this.dataset.saAutoFocus, {once: true});
        this.open(null);
      }
    }
    async request(route, body) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 20000);
      try {
        const form = body instanceof FormData;
        const response = await fetch(this.config.root + route, {
          method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store', signal: abort.signal,
          headers: {Accept: 'application/json', ...(body !== undefined && !form ? {'Content-Type':'application/json'} : {})},
          ...(body !== undefined ? {body: form ? body : JSON.stringify(body)} : {}),
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400 || data.errors) {
          throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : this.strings.network_error);
        }
        return data;
      } catch (error) {
        // A blocked or failed request can answer with an HTML page (Shopify's 429 "Verifying your connection", a 5xx):
        // response.json() then throws a SyntaxError ("Unexpected token '<'"), which shoppers saw verbatim (QA 2026-10-19/20).
        if (error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError) throw new Error(body === undefined ? this.strings.refresh_error : this.strings.network_error);
        throw error;
      } finally { clearTimeout(timeout); }
    }
    icon(name) { return this.querySelector(`[data-sa-icon="${name}"]`)?.innerHTML || ''; }
    syncExpressLayout() {
      const host = this.express?.querySelector('shopify-accelerated-checkout-cart');
      if (!host) return;
      const wallets = [...host.children].filter(el => el.slot?.startsWith('button-'));
      const limit = this.walletMedia.matches ? 3 : 4;
      if (this.walletLayoutHost === host && this.walletLayoutLimit === limit &&
          this.walletLayoutWallets?.length === wallets.length &&
          wallets.every((wallet, index) => this.walletLayoutWallets[index] === wallet)) return;
      this.walletLayoutHost = host;
      this.walletLayoutLimit = limit;
      this.walletLayoutWallets = wallets;
      const visible = Math.min(wallets.length, limit);
      // Keep Shopify's actual wallet elements mounted. Crop only surplus columns;
      // their inert state also removes them from keyboard and assistive navigation.
      // Shopify's horizontal component uses a 10px column gap.
      const ratio = visible ? wallets.length / visible : 1;
      host.style.width = ratio === 1 ? '100%' : `calc(${ratio * 100}% + ${(ratio - 1) * 10}px)`;
      wallets.forEach((wallet, index) => {
        const excluded = index >= limit;
        wallet.inert = excluded;
        if (excluded) wallet.setAttribute('aria-hidden', 'true');
        else wallet.removeAttribute('aria-hidden');
      });
    }
    // Amounts look like the page's prices (owner, 2026-10-20): snippets/space-armory-cart.liquid prints 1234567.89 with
    // Shopify's money filter, so /fr shows "$76.50" like the product page instead of the browser's "76,50 $US". A format
    // this cannot read (no cents, another currency) keeps the browser's own.
    money(cents) {
      const currency = this.store.cart?.currency || this.config.currency;
      const sample = currency === this.config.currency && String(this.config.moneySample || '').match(/^(\D*)1(\D?)234(?:\2)567(\D)89(\D*)$/);
      if (!sample) return new Intl.NumberFormat(this.config.locale, {style:'currency', currency}).format(cents / 100);
      const [whole, part] = (Math.abs(cents) / 100).toFixed(2).split('.');
      // Colombia (owner, 2026-10-23): whole pesos drop Shopify's ",00" ("$329.800 COP"), like the product page and the sticky bar.
      const cents00 = this.config.hideZeroCents && part === '00';
      return `${cents < 0 ? '-' : ''}${sample[1]}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, sample[2])}${cents00 ? '' : sample[3] + part}${sample[4]}`;
    }
    image(product, width) {
      const source = product.featured_image?.url || product.featured_image || product.image?.url || product.image?.src || product.image || product.images?.[0]?.src || product.images?.[0];
      if (!source) return '';
      try { const url = new URL(source, location.origin); url.searchParams.set('width', width * 2); return safeURL(url.href); } catch { return ''; }
    }
    async loadProduct(handle) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      if (!this.productRequests.has(handle)) {
        const request = this.request(`products/${encodeURIComponent(handle)}.js`).then(product => {
          this.productCache.set(handle, product); return product;
        }).finally(() => this.productRequests.delete(handle));
        this.productRequests.set(handle, request);
      }
      return this.productRequests.get(handle);
    }
    async changeFromCartPage(key, line, quantity, sections) {
      if (!key) { await this.store.refresh(); key = this.store.cart.items[Number(line) - 1]?.key; }
      if (!key) throw new Error(this.strings.refresh_error);
      await this.store.setQuantity(key, quantity);
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('sections', sections.join(','));
      const response = await fetch(url, {cache:'no-store', credentials:'same-origin'});
      const rendered = await response.json();
      return {text: async () => JSON.stringify({...this.store.cart, sections: rendered})};
    }
    getSectionsToRender() { return [{id:'cart-icon-bubble'}]; }
    setActiveElement(element) { this.activeElement = element; }
    addForm(formData, options) { this.clearError(); return this.store.addForm(formData, options); }
    renderContents() { this.open(this.activeElement, false); }
    open(trigger, refresh = true) {
      if (trigger && !this.contains(trigger)) this.setActiveElement(trigger);
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('sa-closing');
      if (this.backdrop) { this.backdrop.hidden = false; this.backdrop.classList.remove('sa-closing'); }
      this.updatePreviewOffset();
      if (!this.dialog.open) {
        this.scrollToTop = true;
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialog.show();
        this.dialog.querySelector('[data-sa-close]').focus({preventScroll:true});
        document.dispatchEvent(new CustomEvent('space-armory:cart-open'));
      }
      if (this.store.mutating && !merchandise(this.store.cart || {items:[]}, this.config).length) this.body.innerHTML = `<p class="sa-cart-loading">${escape(this.strings.working)}</p>`;
      if (refresh || !this.store.cart) { this.clearError(); this.store.refresh({maxAge:15000}).catch(() => {}); }
    }
    close() { this.closeDialog(this.dialog); }
    // Checkout+ info or the delivery popup, shown over the open drawer.
    popupOpen() { return !!(this.info?.open || this.deliveryInfo?.open); }
    closeDialog(dialog) {
      if (!dialog.open || dialog.classList.contains('sa-closing')) return;
      if (dialog === this.dialog) for (const popup of [this.info, this.deliveryInfo]) if (popup?.open) popup.close();
      if (dialog === this.dialog) this.dockOpen = undefined;
      dialog.classList.add('sa-closing');
      if (dialog === this.dialog) this.backdrop?.classList.add('sa-closing');
      const delay = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200;
      const timer = setTimeout(() => {
        dialog.close(); dialog.classList.remove('sa-closing');
        if (dialog === this.dialog) {
          if (this.backdrop) this.backdrop.hidden = true;
          document.body.style.overflow = this.previousOverflow || '';
          if (this.activeElement?.isConnected) this.activeElement.focus({preventScroll:true});
          document.dispatchEvent(new CustomEvent('space-armory:cart-close'));
        } else (dialog === this.deliveryInfo ? this.body.querySelector('[data-sa-delivery]') : this.footer.querySelector('[data-sa-info]'))?.focus({preventScroll:true});
      }, delay);
      if (dialog === this.dialog) this.closeTimer = timer;
    }
    updatePreviewOffset() {
      const preview = document.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe');
      const rect = preview?.getBoundingClientRect();
      const offset = rect && rect.width && rect.height && rect.bottom >= innerHeight - 2 && rect.top > innerHeight / 2 ? innerHeight - rect.top : 0;
      this.style.setProperty('--sa-cart-preview-bottom', `${Math.max(0, offset)}px`);
    }
    setBusy(busy) {
      busy = busy || !!this.profileUpdating;
      const locked = busy && this.store.mutating > 0 || this.navigating || this.profileUpdating;
      this.body.setAttribute('aria-busy', String(busy));
      this.dialog.dataset.saBusy = String(busy);
      this.dialog.dataset.saMutating = String(!!locked);
      // A tapped button is disabled while its change runs, and the browser then drops focus to the page, where Escape no
      // longer reaches the drawer (QA 2026-10-20). Remember it so render() puts focus back inside the drawer.
      const active = document.activeElement;
      if (locked && active?.matches?.('[data-sa-mutates]') && this.contains(active)) this.lostFocus = {key: active.dataset.key, action: active.dataset.saAction, protection: active.hasAttribute('data-sa-protection')};
      this.querySelectorAll('[data-sa-mutates]').forEach(button => {
        button.disabled = locked || button.dataset.unavailable === 'true';
        button.setAttribute('aria-busy', String(!!locked));
        if (!locked) delete button.dataset.saLoading;
      });
      if (this.express) {
        const disabled = busy || this.navigating || this.store.unconfirmed || !checkoutReady(this.store.cart, this.config);
        this.express.inert = !!disabled;
        this.express.setAttribute('aria-disabled', String(!!disabled));
        this.express.setAttribute('aria-busy', String(!!locked));
      }
      if (!busy && this.pendingFocus?.isConnected && this.dialog.open && !this.popupOpen()) {
        this.pendingFocus.focus({preventScroll:true});
        this.pendingFocus = null;
      }
    }
    showError(message) {
      const error = this.querySelector('[data-sa-error]');
      error.textContent = message;
      error.hidden = false;
      if (!this.store.cart) {
        this.body.innerHTML = `<div class="sa-cart-empty"><button type="button" class="sa-cart-checkout" data-sa-retry>${escape(this.strings.retry)}</button></div>`;
      }
    }
    clearError() { this.querySelector('[data-sa-error]').hidden = true; }
    render(cart) {
      if (!cart) return;
      const items = merchandise(cart, this.config);
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      const lost = this.lostFocus && (!document.activeElement || document.activeElement === document.body) ? this.lostFocus : null;
      this.lostFocus = null;
      const focused = lost || document.activeElement?.dataset;
      const focusKey = focused?.key;
      const focusAction = lost ? lost.action : focused?.saAction;
      const focusProtection = lost ? lost.protection : document.activeElement?.hasAttribute('data-sa-protection');
      const scroll = this.body.scrollTop;
      this.querySelector('[data-sa-count]').textContent = count;
      if (items.length) this.loadDelivery();
      this.dropLoneMounts(items);
      this.renderReward(cart, items);
      this.body.innerHTML = this.renderUndo() + (items.length
        ? items.map(item => this.renderItem(item)).join('') + this.renderAssurance(items) + (this.dock ? '' : this.renderUpsells(items))
        : `<div class="sa-cart-empty">${this.icon('helmet-off')}<h3>${escape(this.strings.empty)}</h3>${this.emptyNote()}<button type="button" class="sa-cart-checkout sa-cart-continue" data-sa-close>${escape(this.strings.continue_shopping)}</button>${this.renderSuggestions()}</div>`);
      this.body.scrollTop = this.scrollToTop ? 0 : scroll;
      this.scrollToTop = false;
      this.renderDock(items);
      this.footer.hidden = !items.length;
      if (items.length) this.renderFooter(cart);
      if (this.deliveryInfo?.open) this.renderDeliveryInfo(cart);
      this.setBusy(this.store.pending > 0);
      this.querySelector('[data-sa-status]').textContent = `${this.strings.updated}. ${count}`;
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (bubble) {
        bubble.hidden = !count;
        const visible = bubble.querySelector('[aria-hidden]'); if (visible) visible.textContent = count < 100 ? count : '';
        const sr = bubble.querySelector('.visually-hidden'); if (sr) sr.textContent = String(count);
      } else if (count) {
        document.querySelector('#cart-icon-bubble')?.insertAdjacentHTML('beforeend', `<div class="cart-count-bubble"><span aria-hidden="true">${count < 100 ? count : ''}</span><span class="visually-hidden">${count}</span></div>`);
      }
      if (focusKey && this.dialog.open && !this.popupOpen()) {
        const next = [...this.querySelectorAll('[data-sa-action]')].find(element => element.dataset.key === focusKey && element.dataset.saAction === focusAction);
        this.pendingFocus = next || this.dialog.querySelector('[data-sa-close]');
      }
      if (focusProtection && this.dialog.open && !this.popupOpen()) this.pendingFocus = this.querySelector('[data-sa-protection]');
      const missing = items.filter(item => !this.productCache.has(item.handle));
      if (missing.length) Promise.allSettled(missing.map(item => this.loadProduct(item.handle))).then(results => {
        if (this.store.cart === cart && results.some(result => result.status === 'fulfilled')) this.render(cart);
      });
    }
    // The Wear It or Display It mount (armory-display-system, snippets/sa-display-upgrade.liquid) is only sold with a mask:
    // its photo is a mask on the mount. When the last mask leaves the cart, the mount goes too (audit, 2026-10-26), and the
    // mask's Undo puts it back. Each line is tried once while it is in the cart, so a failed removal never loops.
    dropLoneMounts(items) {
      const mount = 'armory-display-system';
      const wearable = item => item.handle !== mount && (this.sized(item) || (item.options_with_values || []).some(option => /\b(size|taille|talla|größe|taglia)\b/i.test(String(option.name))));
      const lone = items.filter(item => item.handle === mount);
      if (!lone.length || items.some(wearable)) return;
      this.droppedMounts ??= new Set();
      lone.filter(item => !this.droppedMounts.has(item.key)).forEach(item => {
        this.droppedMounts.add(item.key);
        const back = {id: item.variant_id, quantity: item.quantity, properties: Object.fromEntries(Object.entries(item.properties || {}).filter(([, value]) => value !== null && value !== ''))};
        this.store.setQuantity(item.key, 0).then(() => {
          this.droppedMounts.delete(item.key);
          if (this.lastRemoved) (this.lastRemoved.extra ??= []).push(back);
          this.broadcast();
        }).catch(() => {});
      });
    }
    // Brand language (owner 2026-10-15): "Nothing equipped yet." under "Your cart is empty", next to the helmet with its
    // eyes closed. The owner approved the English line; French, Spanish and German pages (window.SA_I18N) have no
    // approved translation, so they show none.
    emptyNote() { return window.SA_I18N ? '' : '<p class="sa-cart-empty-note">Nothing equipped yet.</p>'; }
    profileUpdateButton(item) {
      const state=window.SpaceArmoryFitAccount?.state;
      const rules=window.SpaceArmoryFitRules;
      // The full width is on the hidden _Face Fit Width; lines added before 11 Oct 2026 carry it on Measured Size itself.
      if(state?.status!=='ready' || !rules?.valid(state.measurement) || !item.properties?.['Measured Size'] || rules.sameText(item.properties['_Face Fit Width'] || item.properties['Measured Size'],state.measurement))return '';
      // Never on a bundle line (owner, 11 Oct 2026): a bundle names each mask's size ("Hannya mask size", "Demon Oni
      // Mask - Red (Size)"), so updateMeasurement finds no Size option and always failed. Read from the option names,
      // like the product page (global.js), never from the product type. Mask lines are unchanged.
      const bundleSize=option=>{const name=String(option.name).replace(/[()]/g,' ').trim();return /( (size|fit|beden)$|^(taille|talla|größe|groesse|taglia) \S)/i.test(name) && !/^(size|fit|beden|size fit|taille|talla|größe|groesse|taglia)$/i.test(name);};
      if((item.options_with_values || []).some(bundleSize))return '';
      // Only for a different size (owner, 11 Oct 2026): on a line already in the saved size only the number would change.
      const size=rules.sizeFor(state.measurement.cm);
      const aliases={S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
      const lineSize=(item.options_with_values || []).find(option=>/^(size|fit|beden|size fit|taille|talla|größe|groesse|taglia)$/i.test(String(option.name).trim()))?.value;
      if(lineSize!=null && aliases[size]?.includes(String(lineSize).trim().toLowerCase()))return '';
      return `<button type="button" class="sa-cart-profile-update" data-sa-mutates data-sa-profile-update data-key="${escape(item.key)}">${escape(saText('cart.use_saved', 'Use my saved measurement — {size}', {size: rules.sizeFor(state.measurement.cm)}))}</button>`;
    }
    async updateMeasurement(key) {
      if(this.store.pending || this.profileUpdating)return;
      this.profileUpdating=true;
      this.setBusy(true);
      try {
        const state=await window.SpaceArmoryFitAccount.refresh();
        const rules=window.SpaceArmoryFitRules;
        if(!rules.valid(state.measurement))throw new Error(saText('cart.no_saved', 'No saved measurement is available.'));
        await this.store.refresh();
        const item=this.store.cart.items.find(item=>item.key===key);
        if(!item)throw new Error(saText('cart.item_changed', 'This item changed. Please review your cart.'));
        const product=await this.loadProduct(item.handle);
        const index=product.options.findIndex(option=>/^(size|fit|beden|size fit|taille|talla|größe|groesse|taglia)$/i.test(typeof option==='string'?option:option.name));
        const original=product.variants.find(variant=>String(variant.id)===String(item.variant_id));
        const size=rules.sizeFor(state.measurement.cm);
        const aliases={S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
        const options=variant=>variant.options || [variant.option1,variant.option2,variant.option3].filter(value=>value!=null);
        const target=original && index>=0 && product.variants.find(variant=>options(variant).every((value,i)=>i===index?aliases[size].includes(value.toLowerCase()):value===options(original)[i]));
        if(!target?.available)throw new Error(saText('cart.saved_unavailable', 'Your saved size is unavailable in this configuration. Your item has not changed.'));
        const check=await window.SpaceArmoryFitAccount.refresh();
        if(check.customer!==state.customer || check.revision!==state.revision)throw new Error(saText('cart.saved_changed', 'Your saved measurement changed. Please try again.'));
        const stats = Object.fromEntries((rules.scanStatsKeys || []).map(key => [key,null]));
        Object.assign(stats, rules.scanStatsProperties?.(state.measurement.scanStats));
        await this.store.swapVariant(key,target.id,{'Measured Size':this.measuredSizeText(rules.text(state.measurement)),'_Face Fit Width':rules.text(state.measurement),'_Face Fit Profile Revision':String(state.revision),'_Face Fit Measurement':null,'_Face Fit Device':null,'_Face Fit Source':`saved_profile | ${rules.version}`,'_Face Fit Action':'Use saved measurement in cart','_Size Selection Method':'Saved measurement applied in cart',...stats});
        this.broadcast();
      } catch(error){this.showError(error.message);} finally {this.profileUpdating=false;this.setBusy(this.store.pending>0);}
    }
    // The order line "Measured Size" in the visitor's unit only, decided in Liquid (snippets/sa-size-unit.liquid,
    // config.sizeUnit): "5.67 in - L" on the United States country, "14.4 cm - L" elsewhere. Lines added before 11 Oct
    // 2026 stored both units ("14.4 cm / 5.67 in - L", face-fit-rules.js text()) and show the same way; newer lines are
    // stored in the visitor's unit (both units on the hidden _Face Fit Width) and, like any other shape, print as stored
    // (snippets/sa-cart-property.liquid does the same on the cart page).
    measuredSizeText(value) {
      const match = String(value).match(/^(\d+(?:\.\d+)?) cm \/ (\d+(?:\.\d+)?) in - (S|M|L|XL|XXL)$/);
      if (!match) return value;
      return this.config.sizeUnit === 'in' ? `${match[2]} in - ${match[3]}` : `${match[1]} cm - ${match[3]}`;
    }
    // French, Spanish and German pages: the discount's title from window.SA_I18N ("discount.HALLOWEEN 15% OFF MASKS").
    discountTitle(title) { return saText(`discount.${String(title || '').trim()}`, this.titleCase(title)); }
    titleCase(text) {
      const value = String(text || '');
      if (value !== value.toUpperCase()) return value;
      const minor = new Set(['a', 'an', 'and', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to']);
      return value.toLowerCase().split(' ').map((word, index) => index && minor.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    recordRecentlyViewed() {
      const match = location.pathname.match(/\/products\/([^/?#]+)/);
      if (!match) return;
      try {
        const handles = this.recentHandles().filter(handle => handle !== match[1]);
        localStorage.setItem('sa_recent_products_v1', JSON.stringify([match[1], ...handles].slice(0, 6)));
      } catch {}
    }
    recentHandles() {
      try { return JSON.parse(localStorage.getItem('sa_recent_products_v1') || '[]').filter(handle => typeof handle === 'string'); } catch { return []; }
    }
    loadDelivery() {
      if (this.deliveryRequested || !this.config.deliveryModule) return;
      this.deliveryRequested = true;
      this.loadDeliveryStyles();
      this.importModule(this.config.deliveryModule).then(module => {
        const zone = module.zoneForCountry(this.config.countryCode);
        this.deliveryModule = module;
        this.deliveryZone = zone;
        // Colombia: "Desde $48.900" and the popup's prices in the drawer's own format (hideZeroCents is COP-only).
        if (this.config.hideZeroCents) module.useShopMoneyFormat?.(this.config.currency, this.config.moneySample, true);
        // Paid shipping: the popup's own price for the cheapest method ("From CHF 8") instead of "At checkout".
        const conversion = {baseCurrency: this.config.baseCurrency, activeCurrency: window.Shopify?.currency?.active, rate: Number(window.Shopify?.currency?.rate)};
        this.shippingFrom = module.shippingFromLabel?.(zone, this.config.currency, conversion) || null;
        const method = zone?.methods.find(candidate => candidate.kind === 'standard') ?? zone?.methods[0];
        const range = method && module.deliveryWindow(method, this.config.processingMin, this.config.processingMax);
        if (range) {
          this.deliveryText = saText('cart.arrives', 'Arrives {dates}', {dates: module.formatDateWindow(range)});
          this.deliveryStandard = method.kind === 'standard';
        }
        // The reward bar's free FedEx Express: the same estimate the product page shows for that method.
        const express = this.config.reward?.expressMethod && zone?.methods.find(candidate => candidate.name === this.config.reward.expressMethod);
        const expressRange = express && module.deliveryWindow(express, this.config.processingMin, this.config.processingMax);
        if (expressRange) this.expressText = saText('cart.arrives', 'Arrives {dates}', {dates: module.formatDateWindow(expressRange)});
        if ((range || this.shippingFrom) && this.store.cart && !this.store.pending) this.render(this.store.cart);
      }).catch(() => {});
    }
    importModule(url) { return import(url); }
    // The delivery popup uses the product page's styles; product pages already have them.
    loadDeliveryStyles() {
      const links = (this.config.deliveryStyles || []).map(href => {
        const url = new URL(href, location.href).href;
        const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.href === url);
        if (existing) return null;
        const link = Object.assign(document.createElement('link'), {rel: 'stylesheet', href: url});
        const loaded = new Promise(resolve => { link.addEventListener('load', resolve, {once: true}); link.addEventListener('error', resolve, {once: true}); });
        document.head.append(link);
        return loaded;
      }).filter(Boolean);
      this.deliveryStylesReady = Promise.all(links);
    }
    // Owner, 2026-10-21: the delivery line opens the product page's delivery popup over the drawer, filled by the same
    // module for this cart: the amount is what the shopper pays for the items after discounts (the shipping rates'
    // condition), so from $84 (US) / £75 (UK) USPS goes and FedEx Express reads FREE, as at checkout.
    async openDelivery() {
      const popup = this.deliveryInfo;
      if (!popup || popup.open || !this.deliveryModule || !this.deliveryZone || !this.store.cart) return;
      await Promise.race([this.deliveryStylesReady, new Promise(resolve => setTimeout(resolve, 1500))]);
      if (popup.open || !this.dialog.open) return;
      this.renderDeliveryInfo(this.store.cart);
      popup.querySelector('.delivery-estimate__sheet').scrollTop = 0;
      popup.showModal();
      popup.querySelector('[data-sa-delivery-close]')?.focus({preventScroll:true});
    }
    renderDeliveryInfo(cart) {
      const popup = this.deliveryInfo;
      const items = merchandise(cart, this.config);
      const reward = this.rewardExpress && this.rewardFor(this.config.countryCode);
      const total = Math.max(this.paidCents(cart, items), reward ? reward.express : 0) / 100;
      this.deliveryModule.renderDeliverySheet(popup, {
        zone: this.deliveryZone, countryCode: this.config.countryCode, countryName: this.config.countryName || this.config.countryCode,
        currencyCode: this.config.currency, baseCurrency: this.config.baseCurrency, taxRules: this.config.taxRules,
        processingMin: this.config.processingMin, processingMax: this.config.processingMax, price: total, orderTotal: total,
        logoFor: carrier => popup.getAttribute(`data-carrier-${carrier}-logo`),
      });
      // As on the product page: "Handcrafted · Ships in 2–5 business days" for masks, the returns copy unless every item is personalised.
      const preparation = popup.querySelector('[data-sa-delivery-preparation]');
      if (preparation) preparation.hidden = !items.some(item => this.sized(item));
      const returns = popup.querySelector('[data-sa-delivery-returns-copy]');
      if (returns) returns.hidden = !items.some(item => this.returnsEligible(item));
    }
    // Masks are the items with a size (on 2026-10-21 all 313 masks have one; the beret, gaiters, balaclava and gift card none).
    // The product type cannot tell: /fr reads the balaclava's type "Masques" too (snippets/sa-product-type.liquid).
    sized(item) { return (item.options_with_values || []).some(option => /^(size|fit|beden|size fit|taille|talla|größe|groesse|taglia)$/i.test(String(option.name).trim())); }
    // The product page's rule (snippets/delivery-estimate.liquid): no returns copy for custom or personalised items.
    // A Custom Color Studio build (sections/sa-custom-color.liquid) is painted to order: no free exchange (owner, 2026-10-26).
    returnsEligible(item) {
      if (!item.requires_shipping) return false;
      if (item.properties && item.properties['Custom Paint Order']) return false;
      const tags = (this.productCache.get(item.handle)?.tags || []).map(tag => String(tag).toLowerCase().replaceAll('_', '-'));
      if (tags.some(tag => ['custom', 'custom-order', 'custom-painted', 'personalized', 'personalised', 'final-sale'].includes(tag))) return false;
      return !/personali[sz]ed|custom-painted/i.test(`${item.handle} ${item.product_title || ''}`);
    }
    // What the shopper pays for the items after discounts: the shipping rates' condition and the reward bar's first step.
    paidCents(cart, items) {
      const orderOff = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + (discount.total_allocated_amount || 0), 0);
      return items.reduce((sum, item) => sum + item.final_line_price, 0) - orderOff;
    }
    // Set by assets/sa-visitor-country.js for a visitor from a country outside every market (shown the US store):
    // the US options stay, for shoppers who ship to a US address, with "US addresses only — we don't ship to … yet."
    noShip() {
      const visitor = window.SpaceArmoryNoShip;
      return visitor?.code && visitor.line && visitor.code !== this.config.countryCode ? visitor : null;
    }
    // Owner, 2026-10-13: no "Taxes included." in the cart (checkout shows the taxes). Owner, 2026-10-16: a cart over
    // €150 / £135 before VAT says that checkout charges no VAT and the carrier collects it at delivery: the same
    // sentence and arithmetic as the product pages and the cart page (snippets/sa-tax-note.liquid, taxRules).
    overLimitLine(cart) {
      const rules = this.config.taxRules || {};
      const country = this.config.countryCode;
      const currency = this.config.currency;
      const vat = String(rules.vat || '').split(',');
      if (country !== 'GB' && !vat.includes(country)) return '';
      const pairs = String((country === 'GB' ? rules.ukLimits : rules.euLimits) || '').split(',').map(pair => pair.split(':'));
      const limit = Number(pairs.find(([code]) => code === currency)?.[1]);
      const rate = Number(String(rules.vatRates || '').split(',').map(pair => pair.split(':')).find(([code]) => code === country)?.[1] || 0);
      const label = String(rules.limitLabels || '').split('|').find(pair => pair.split(':')[0] === currency);
      if (!Number.isFinite(limit) || limit < 0 || !label || !rules.overLimit) return '';
      // Before VAT without fractions: total ÷ (1 + rate) > limit  ⇔  total × 1000 > limit × (1000 + rate in ‰).
      if (cart.total_price * 1000 <= limit * (1000 + rate)) return '';
      return rules.overLimit.replace('[limit]', label.slice(label.indexOf(':') + 1));
    }
    // Mexico (owner, 2026-10-24): the short SAT import-tax / RFC-CURP line above the checkout button, the same sentence as
    // the cart page (snippets/sa-tax-note.liquid, taxRules.mxCart).
    mexicoLine() {
      return this.config.countryCode === 'MX' ? String(this.config.taxRules?.mxCart || '') : '';
    }
    // Only a visitor from a country outside every market sees "US addresses only — we don't ship to … yet." instead.
    renderTaxNote(cart) {
      if (!merchandise(cart, this.config).some(item => item.requires_shipping)) return '';
      const text = this.noShip()?.line || this.overLimitLine(cart) || this.mexicoLine();
      return text ? `<p class="sa-cart-tax-note" data-sa-tax-note>${escape(text)}</p>` : '';
    }
    // Owner, 2026-10-26: "Wrong Size?" only while a sized item can still be exchanged (not for a custom-painted build alone),
    // and no delivery line for a cart with nothing to ship (a digital gift card alone).
    renderAssurance(items) {
      const exchange = items.some(item => this.sized(item) && this.returnsEligible(item));
      const ships = items.some(item => item.requires_shipping);
      const rows = [];
      const delivery = !ships ? null : this.rewardExpress && this.expressText ? [this.expressText, saText('cart.express_suffix', ' · Free FedEx Express')]
        : this.deliveryText ? [this.deliveryText, this.deliveryStandard ? saText('cart.standard_suffix', ' · Standard Shipping') : ''] : null;
      // The whole line opens the delivery popup (owner, 2026-10-21: shoppers kept tapping it); the (i) sits at its end like Checkout+'s.
      const text = delivery && `<span><strong>${escape(delivery[0])}</strong>${escape(delivery[1])}</span>`;
      if (delivery && this.deliveryInfo && this.deliveryModule) rows.push(`<li><button type="button" class="sa-cart-delivery" data-sa-delivery aria-haspopup="dialog" aria-controls="SaDeliveryInfo">${this.icon('truck')}${text}<span class="sa-cart-info-button sa-cart-delivery-info" aria-hidden="true">${this.icon('info')}</span></button></li>`);
      else if (delivery) rows.push(`<li>${this.icon('truck')}${text}</li>`);
      if (exchange) rows.push(`<li>${this.icon('exchange')}<span>${saText('cart.wrong_size', '<strong>Wrong Size?</strong> Free exchange within 30 days.')}</span></li>`);
      return rows.length ? `<ul class="sa-cart-assure" role="list">${rows.join('')}</ul>` : '';
    }
    // Owner, 2026-10-18: step 1 free FedEx Express, step 2 25% off (an automatic discount that combines with nothing, so it
    // replaces Halloween 15%). Read-only: it never touches the cart. Step 1 counts what the shopper pays for the items after
    // discounts (the shipping rate's condition); step 2 counts config.reward.discountBasis ("list" = before discounts, the
    // automatic discount's minimum) and shows unlocked only while Shopify has really applied the discount.
    // Owner, 2026-10-21: the UK gets the same bar in GBP. A market sets its own currency and FedEx threshold (its zone's
    // rate condition); without its own discount minimum it uses the USD one at Shopify's rate, rounded up to the penny,
    // because the automatic discount's minimum is converted the same way.
    rewardFor(country) {
      const base = this.config.reward;
      if (!base) return null;
      if (base.countries?.includes(country)) return base;
      const market = base.markets?.[country];
      if (!market) return null;
      const rate = Number(window.Shopify?.currency?.rate);
      const discount = market.discount ?? (Number.isFinite(rate) && rate > 0 ? Math.ceil(base.discount * rate) : NaN);
      return Number.isFinite(discount) ? {...base, ...market, discount} : null;
    }
    renderReward(cart, items) {
      const reward = this.rewardFor(this.config.countryCode);
      // Nothing to ship (a digital gift card alone): no FedEx Express step, and Shopify never discounts a gift card (audit, 2026-10-26).
      const on = !!(reward?.enabled && this.reward && items.some(item => item.requires_shipping) && this.config.currency === reward.currency);
      const before = this.rewardTier;
      this.rewardExpress = false;
      this.rewardTier = 0;
      if (!this.reward) return;
      if (!on) { this.reward.hidden = true; this.reward.innerHTML = ''; return; }
      const title = String(reward.discountTitle || '').trim().toLowerCase();
      const matches = value => String(value || '').trim().toLowerCase() === title;
      const paid = this.paidCents(cart, items);
      const list = items.reduce((sum, item) => sum + item.original_line_price, 0);
      const discounted = !!title && ((cart.cart_level_discount_applications || []).some(discount => matches(discount.title))
        || items.some(item => (item.line_level_discount_allocations || []).some(allocation => matches(allocation.discount_application?.title))));
      const toDiscount = reward.discount - (reward.discountBasis === 'paid' ? paid : list);
      this.rewardExpress = discounted || paid >= reward.express;
      this.rewardTier = discounted ? 2 : this.rewardExpress ? 1 : 0;
      const done = text => `<span class="sa-cart-reward-done">${escape(text)}</span>`;
      // The amount is bold wherever the language puts it ("Plus que {amount} pour…", "{amount} more for…").
      const amount = (key, fallback, cents) => { const [head, tail = ''] = saText(key, fallback).split('{amount}'); return `<span>${escape(head)}<strong>${escape(this.money(cents))}</strong>${escape(tail)}</span>`; };
      let text;
      if (this.rewardTier === 0) text = amount('cart.reward_away', '{amount} away from free FedEx Express', reward.express - paid) + `<span>${escape(this.money(reward.express))}</span>`;
      else if (this.rewardTier === 1) text = done(saText('cart.reward_express', 'Free FedEx Express ✓')) + (toDiscount > 0 ? amount('cart.reward_more', '{amount} more for 25% off', toDiscount) : '');
      else text = done(saText('cart.reward_all', 'Free FedEx Express ✓ · 25% off ✓'));
      const mark = Math.min(100, reward.express / reward.discount * 100);
      const width = this.rewardTier === 2 ? 100 : this.rewardTier === 1 ? Math.max(mark, Math.min(99, (reward.discount - Math.max(0, toDiscount)) / reward.discount * 100)) : Math.min(mark, paid / reward.discount * 100);
      this.reward.innerHTML = `<div class="sa-cart-reward-text">${text}</div><div class="sa-cart-reward-line" aria-hidden="true"><i class="sa-cart-reward-fill" style="width:${width.toFixed(2)}%"></i><i class="sa-cart-reward-mark" style="left:${mark.toFixed(2)}%"></i></div>`;
      this.reward.dataset.tier = String(this.rewardTier);
      this.reward.hidden = false;
      if (before !== undefined && this.rewardTier > before) {
        this.reward.classList.remove('sa-cart-reward-up');
        void this.reward.offsetWidth;
        this.reward.classList.add('sa-cart-reward-up');
      }
    }
    renderUndo() {
      if (!this.lastRemoved) return '';
      return `<div class="sa-cart-undo" role="status"><span>${escape(saText('cart.removed', 'Removed {title}', {title: this.lastRemoved.title}))}</span><button type="button" data-sa-undo>${escape(saText('cart.undo', 'Undo'))}</button></div>`;
    }
    showUndo(removed) {
      clearTimeout(this.undoTimer);
      this.lastRemoved = removed;
      this.undoTimer = setTimeout(() => { this.lastRemoved = null; if (this.store.cart) this.render(this.store.cart); }, 8000);
      if (this.store.cart) this.render(this.store.cart);
    }
    async undoRemove() {
      const removed = this.lastRemoved;
      if (!removed || this.store.mutating || this.navigating) return;
      clearTimeout(this.undoTimer);
      this.lastRemoved = null;
      this.clearError();
      try {
        await this.store.add(removed.id, removed.quantity, removed.properties, {protection: removed.protection});
        for (const extra of removed.extra || []) await this.store.add(extra.id, extra.quantity, extra.properties);
        this.broadcast();
      } catch (error) {
        this.showError(error.message);
        this.render(this.store.cart);
      }
    }
    renderSuggestions() {
      if (!this.suggestions) { this.loadSuggestions(); return ''; }
      const {heading, products} = this.suggestions;
      if (!products.length) return '';
      return `<section class="sa-cart-suggest"><h4>${escape(heading)}</h4><ul role="list">${products.map(product => {
        const display = this.cartTitle({product_title: product.title});
        const url = product.url ? safeURL(product.url) : `${this.config.root}products/${encodeURIComponent(product.handle)}`;
        return `<li><a href="${url}"><img src="${this.image(product, 52)}" width="52" height="52" alt="" loading="lazy"><span><strong>${escape(display.name)}</strong>${display.color ? `<small>${escape(display.color)}</small>` : ''}</span>${this.icon('chevron')}</a></li>`;
      }).join('')}</ul></section>`;
    }
    async loadSuggestions() {
      if (this.suggestionsLoading) return;
      this.suggestionsLoading = true;
      let heading = saText('cart.recently_viewed', 'Recently Viewed');
      let products = [];
      try {
        const handles = this.recentHandles().filter(handle => !/shipping-protection/i.test(handle)).slice(0, 3);
        if (handles.length) {
          const results = await Promise.allSettled(handles.map(handle => this.loadProduct(handle)));
          products = results.filter(result => result.status === 'fulfilled' && result.value?.handle).map(result => result.value);
        }
        if (!products.length && this.config.suggestCollection) {
          heading = saText('cart.popular', 'Popular Right Now');
          const data = await this.request(`collections/${encodeURIComponent(this.config.suggestCollection)}/products.json?limit=3`);
          products = data.products || [];
        }
      } catch {}
      this.suggestions = {heading, products: products.slice(0, 3)};
      if (this.store.cart && !merchandise(this.store.cart, this.config).length) this.render(this.store.cart);
    }
    cartTitle(item) {
      const full = String(item.product_title || item.title || '');
      const match = full.match(/^(.*?)\s+[-–—]\s+(.+)$/);
      return match ? {name: match[1].trim(), color: match[2].trim()} : {name: full, color: ''};
    }
    variantParts(product, variant, fallback) {
      const values = variant?.options || [variant?.option1, variant?.option2, variant?.option3].filter(value => value != null);
      const names = (product?.options || []).map(option => typeof option === 'string' ? option : option.name);
      if (!names.length || names.length !== values.length) return [fallback || variant?.title || ''];
      return names.map((name, index) => {
        const normalized = name.trim();
        const label = /^(size|taille|talla|größe|groesse|taglia)$/i.test(normalized) ? saText('cart.size', 'Size') : normalized === 'Elastic Buckle - Straw Rope ?' ? saText('cart.strap', 'Strap') : optionName(normalized);
        return /^title$/i.test(label) ? values[index] : saText('cart.option_line', '{name}: {value}', {name: label, value: optionValue(values[index])});
      });
    }
    variantText(product, variant, fallback) { return this.variantParts(product, variant, fallback).join(' / '); }
    // A Duo/Trio names every mask's options ("Red mask size: L / Blue mask fangs: Silver / Blue mask size: L"): on one line
    // that wrapped mid-option in the drawer, so three or more options, or a long pair, read one option per line (audit, 2026-10-26).
    variantLabelHTML(parts) {
      const text = parts.join(' / ');
      if (parts.length < 3 && text.length <= 36) return escape(text);
      return `<span class="sa-cart-variant-lines">${parts.map(part => `<span>${escape(part)}</span>`).join('')}</span>`;
    }
    renderItem(item) {
      const product = this.productCache.get(item.handle);
      const variants = product?.variants;
      const display = this.cartTitle(item);
      const title = escape(display.name);
      // The Measured Size line starts with the small helmet: this size came from the shopper's face (owner 2026-10-15).
      const properties = Object.entries(item.properties || {}).filter(([key,value]) => value && !key.startsWith('_')).map(([key,value]) => `<div${key === 'Measured Size' ? ` class="sa-cart-measured">${this.icon('helmet')}` : '>'}${escape(saText('cart.option_line', '{name}: {value}', {name: key === 'Measured Size' ? saText('cart.measured_size', 'Measured Size') : key, value: key === 'Measured Size' ? this.measuredSizeText(value) : value}))}</div>`).join('');
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.name;
      const selectedVariant = variants?.find(variant => String(variant.id) === String(item.variant_id));
      const variantLabel = this.variantLabelHTML(this.variantParts(product, selectedVariant, item.variant_title));
      // A gift card's amount option is a fixed USD label ("Amount: $100"); the line price already shows the shopper's
      // currency, so a EUR, GBP or CAD cart would read two different amounts. No option line for a gift card (audit, 2026-10-26).
      const giftCard = item.gift_card || item.handle === 'space-armory-gift-card';
      let variant = giftCard ? '' : variants?.length > 1 ? `<select class="sa-cart-variant"  data-sa-variant data-sa-mutates data-sa-action="variant" data-key="${escape(item.key)}" aria-label="${escape(this.strings.variant)}: ${title}">${variants.map(variant => `<option value="${variant.id}"${String(variant.id) === String(item.variant_id) ? ' selected' : ''}${!variant.available ? ' disabled' : ''}>${escape(this.variantText(product, variant, variant.title))}${!variant.available ? ` — ${escape(this.strings.sold_out)}` : ''}</option>`).join('')}</select>` : item.variant_title && item.variant_title !== 'Default Title' ? `<span class="sa-cart-variant">${variantLabel}</span>` : '';
      if (!giftCard && variants?.length > 1) {
        variant = `<div class="sa-cart-variant-wrap"><span class="sa-cart-variant-value" aria-hidden="true">${variantLabel}<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="m3 4.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.2"/></svg></span>${variant}</div>`;
      }
      const price = item.final_line_price;
      return `<article class="sa-cart-item" data-sa-line="${escape(item.key)}">
        <a href="${safeURL(item.url)}" tabindex="-1" aria-hidden="true"><img class="sa-cart-image" src="${this.image(item,100)}" width="100" height="100" alt="${title}"></a>
        <div class="sa-cart-item-details"><a class="sa-cart-item-title" href="${safeURL(item.url)}">${title}</a>${display.color ? `<div class="sa-cart-color">${escape(display.color)}</div>` : ''}${variant}
        ${this.profileUpdateButton(item)}
        ${properties || sellingPlan ? `<div class="sa-cart-properties">${properties}${sellingPlan ? `<div>${escape(sellingPlan)}</div>` : ''}</div>` : ''}
        ${(item.line_level_discount_allocations || []).map(discount => `<div class="sa-cart-discount">${this.icon('tag')}<span>${escape(this.discountTitle(discount.discount_application.title))} · −${escape(this.money(discount.amount))}</span></div>`).join('')}
        <div class="sa-cart-item-bottom"><div class="sa-cart-quantity">
          <button type="button" data-sa-mutates data-sa-action="decrease" data-key="${escape(item.key)}" data-unavailable="${item.quantity <= 1}" aria-label="${escape(this.strings.decrease)}: ${title}">${this.icon('minus')}</button><input type="number" inputmode="numeric" min="1" step="1" value="${item.quantity}" style="width:${Math.min(42, Math.max(16, String(item.quantity).length * 10))}px" aria-label="Quantity: ${title}" data-sa-quantity data-sa-mutates data-sa-action="quantity" data-key="${escape(item.key)}">
          <button type="button" data-sa-mutates data-sa-action="increase" data-key="${escape(item.key)}" aria-label="${escape(this.strings.increase)}: ${title}">${this.icon('plus')}</button>
        </div><div class="sa-cart-price">${item.original_line_price > price ? `<s>${escape(this.money(item.original_line_price))}</s>` : ''}${escape(this.money(price))}</div></div></div>
        <button type="button" class="sa-cart-icon-button sa-cart-remove" data-sa-mutates data-sa-action="remove" data-key="${escape(item.key)}" aria-label="${escape(this.strings.remove)}: ${title}">${this.icon('remove')}</button></article>`;
    }
    renderUpsells(items) {
      const cards = this.upsellCards(items);
      return cards ? `<section class="sa-cart-upsells"><h3>${escape(this.config.upsellHeading)}</h3><div class="sa-cart-upsell-list">${cards}</div></section>` : '';
    }
    // Owner, 2026-10-18 (Allbirds-style): the same cards docked above the totals. Open with one line in the cart, folded to its
    // heading from two lines on (and on very small screens); a tap on the heading wins until the drawer closes. The Add buttons are the usual data-sa-add ones.
    renderDock(items) {
      if (!this.dock) return;
      const cards = items.length ? this.upsellCards(items) : '';
      if (!cards) { this.dock.hidden = true; this.dock.innerHTML = ''; return; }
      // Very small screens (under 360px wide or 700px tall) start folded, so the cards never cover the mask; the arrow opens them.
      // 700, not 620 (owner via Gemini QA, 2026-10-24): at 375×667 the open cards hid the Arrives line and "Wrong Size?" in every language.
      const small = window.innerWidth < 360 || window.innerHeight < 700;
      const open = this.dockOpen ?? (items.length <= 1 && !small);
      const shown = !this.dock.hidden && this.dock.dataset.open;
      const from = shown ? this.dockListHeight() : 0;
      this.dock.dataset.open = String(open);
      this.dock.innerHTML = `<button type="button" class="sa-cart-dock-toggle" data-sa-dock-toggle aria-expanded="${open}" aria-controls="SaCartDockList"><span class="sa-cart-dock-label"><span>${escape(this.config.upsellHeading)}</span>${this.dockUnlockBadge(items)}</span><span class="sa-cart-dock-chevron" aria-hidden="true">${this.icon('chevron')}</span></button><div class="sa-cart-upsell-list" id="SaCartDockList"${open ? '' : ' hidden'}>${cards}</div>`;
      this.dock.hidden = false;
      // The fold after an add (one line to two) slides too, like a tap on the heading.
      if (shown && shown !== String(open)) this.slideDock(open, from, true);
    }
    // Owner, 2026-10-23 (Gemini item 6): while the bar still says "{amount} away from free FedEx Express" (tier 0) and one of
    // the docked cards would close that gap on its own, the heading carries "Unlocks FREE FedEx Express". It goes as soon
    // as the threshold is reached (tier 1 or 2), in a market without the bar, or when no card is big enough.
    dockUnlockBadge(items) {
      const cart = this.store.cart;
      const reward = cart && this.reward && this.rewardFor(this.config.countryCode);
      if (!reward?.enabled || this.config.currency !== reward.currency || this.rewardTier !== 0) return '';
      const gap = reward.express - this.paidCents(cart, items);
      if (gap <= 0) return '';
      const unlocks = (this.config.upsells || []).some(product => product?.id && !items.some(item => String(item.product_id) === String(product.id))
        && (product.variants || []).some(variant => variant.available && Number(variant.price) >= gap));
      return unlocks ? `<span class="sa-cart-dock-badge">${escape(saText('cart.dock_unlocks', 'Unlocks FREE FedEx Express'))}</span>` : '';
    }
    dockListHeight() {
      const list = this.dock?.querySelector('.sa-cart-upsell-list');
      return list && !list.hidden ? list.getBoundingClientRect().height : 0;
    }
    // Owner, 2026-10-18: the cards slide open and shut instead of jumping. Only the list height moves; the cart, the
    // buttons and the open state are already final, so a cart update or a second tap mid-slide never leaves it stuck.
    slideDock(open, from, turnChevron) {
      const list = this.dock.querySelector('.sa-cart-upsell-list');
      if (!list) return;
      // The heading moves while the cards slide, so a quick second tap on the arrow can land where + Add now is;
      // the dock's Add buttons ignore taps for half a second after a slide starts (the arrow itself always answers).
      this.dockMovedAt = Date.now();
      list.getAnimations?.().forEach(animation => animation.cancel());
      delete list.dataset.saSliding;
      list.hidden = false;
      const to = open ? list.getBoundingClientRect().height : 0;
      list.hidden = !open;
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (typeof list.animate !== 'function' || still || Math.abs(to - from) < 1) return;
      list.hidden = false;
      list.dataset.saSliding = '';
      const pad = getComputedStyle(list).paddingBottom;
      const slide = list.animate([{height: `${from}px`, paddingBottom: open && !from ? '0px' : pad, opacity: open && !from ? 0 : 1}, {height: `${to}px`, paddingBottom: open ? pad : '0px', opacity: open ? 1 : 0}], {duration: open ? 280 : 240, easing: 'cubic-bezier(.4,0,.2,1)'});
      if (turnChevron) this.dock.querySelector('.sa-cart-dock-chevron svg')?.animate([{transform: `rotate(${open ? -90 : 90}deg)`}, {transform: `rotate(${open ? 90 : -90}deg)`}], {duration: 240, easing: 'ease'});
      // A cancelled slide reports back later; only the newest one may tidy up.
      const token = this.dockSlide = {};
      const done = () => {
        if (this.dockSlide !== token) return;
        delete list.dataset.saSliding;
        list.hidden = this.dock.dataset.open !== 'true';
      };
      slide.onfinish = done;
      slide.oncancel = done;
    }
    toggleDock() {
      const toggle = this.dock?.querySelector('[data-sa-dock-toggle]');
      if (!toggle) return;
      const from = this.dockListHeight();
      const open = this.dock.dataset.open !== 'true';
      this.dockOpen = open;
      this.dock.dataset.open = String(open);
      toggle.setAttribute('aria-expanded', String(open));
      this.slideDock(open, from, false);
    }
    upsellCards(items) {
      const neckwearBundles = new Set(this.config.neckwearBundleProductIds || []);
      if (items.some(item => neckwearBundles.has(String(item.product_id)))) return '';
      // A digital gift card alone gets no "Complete Your Look" (audit, 2026-10-26): nothing ships to wear it with.
      if (!items.some(item => item.requires_shipping)) return '';
      const products = (this.config.upsells || []).filter(product => product?.id && !items.some(item => String(item.product_id) === String(product.id)));
      if (!products.length) return '';
      return products.map(product => {
        const variant = product.variants.find(variant => variant.available) || product.variants[0];
        // Owner, 2026-10-23: a long price ("$26.000" in Colombia) or a long Add label ("+ Ajouter", "+ Hinzufügen") does not fit
        // next to the price in a two-column docked card on a phone; that card puts the button under the price (sa-cart-upsell--stack).
        const stack = this.money(variant.price).length > 6 || `+ ${this.strings.add}`.length > 5;
        return `<article class="sa-cart-upsell${stack ? ' sa-cart-upsell--stack' : ''}"><a href="${this.config.root}products/${encodeURIComponent(product.handle)}" tabindex="-1" aria-hidden="true"><img src="${this.image(product,52)}" alt="${escape(product.title)}" width="52" height="52"></a><div class="sa-cart-upsell-content"><a class="sa-cart-upsell-name" href="${this.config.root}products/${encodeURIComponent(product.handle)}">${escape(product.title)}</a><div class="sa-cart-price">${escape(this.money(variant.price))}</div></div><button type="button" class="sa-cart-add" data-sa-mutates data-sa-add="${variant.id}" data-unavailable="${!variant.available}" aria-label="${escape(variant.available ? this.strings.add : this.strings.sold_out)}: ${escape(product.title)}">${variant.available ? `+ ${escape(this.strings.add)}` : escape(this.strings.sold_out)}</button></article>`;
      }).join('');
    }
    renderShipping(cart) {
      if (!merchandise(cart, this.config).some(item => item.requires_shipping)) return '';
      // Canada (owner, 2026-10-23): no free standard method, so the line reads FREE once the reward bar's FedEx step is reached.
      const free = this.config.freeStandardShippingCountries?.includes(this.config.countryCode) === true || this.rewardExpress;
      const value = free ? `${this.strings.free}${this.noShip() ? ' to US addresses' : ''}` : this.shippingFrom ? saText('cart.shipping_from', 'From {price}', {price: this.shippingFrom}) : this.strings.at_checkout;
      return `<div class="sa-cart-shipping"><dt>${escape(this.strings.shipping)}</dt><dd data-sa-shipping>${escape(value)}</dd></div>`;
    }
    renderFooter(cart) {
      const target = protectionVariant(cart, this.config);
      const coverage = cart.items.filter(item => isCoverage(item, this.config));
      const fee = coverage.length ? coverage.reduce((sum,item) => sum + item.final_line_price,0) : target?.price || 0;
      const selected = coverage.length > 0;
      const protectionIcon = this.icon('protection');
      const subtotal = merchandise(cart, this.config).reduce((sum, item) => sum + item.final_line_price, 0);
      const discounts = (cart.cart_level_discount_applications || []).map(discount => `<p class="sa-cart-discount">${this.icon('tag')}<span>${escape(this.discountTitle(discount.title))} · −${escape(this.money(discount.total_allocated_amount))}</span></p>`).join('');
      const lineSavings = merchandise(cart, this.config).reduce((sum, item) => sum + Math.max(0, item.original_line_price - item.final_line_price), 0);
      const cartSavings = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + (discount.total_allocated_amount || 0), 0);
      const savings = lineSavings + cartSavings;
      this.summary.innerHTML = `<dl class="sa-cart-totals"><div><dt>${escape(this.strings.subtotal)}</dt><dd data-sa-subtotal>${escape(this.money(subtotal))}</dd></div>${savings > 0 ? `<div class="sa-cart-savings"><dt>${escape(saText('cart.you_save', 'You Save'))}</dt><dd>${escape(this.money(savings))}</dd></div>` : ''}${this.renderShipping(cart)}</dl>${this.renderTaxNote(cart)}${discounts}${target || selected ? `<div class="sa-cart-protection">${protectionIcon}<div class="sa-cart-protection-copy"><div class="sa-cart-protection-heading">Checkout+ <button type="button" class="sa-cart-icon-button sa-cart-info-button" data-sa-info aria-label="${escape(this.strings.info)}">${this.icon('info')}</button></div><p>${escape(this.config.protectionText)}</p></div><span class="sa-cart-protection-price">${escape(this.money(fee))}</span><button type="button" class="sa-cart-switch" role="switch" aria-checked="${selected}" aria-label="${escape(this.strings.protection_toggle)}" data-sa-protection data-sa-mutates><span class="sa-cart-switch-spinner" aria-hidden="true"></span></button></div>` : ''}
        <button type="button" class="sa-cart-checkout" data-sa-mutates data-sa-checkout="${selected ? 'protected' : 'standard'}">${this.icon('lock')}<span>${selected ? 'Checkout+' : escape(this.strings.checkout)} • ${escape(this.money(cart.total_price))}</span></button>`;
    }
    broadcast() {
      if (typeof publish === 'function') Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {source:'space-armory-cart', cartData:this.store.cart})).catch(console.error);
      this.dispatchEvent(new CustomEvent('space-armory:cart-updated', {bubbles:true, detail:{cart:this.store.cart}}));
    }
    async handleClick(event) {
      if (event.target.closest('[data-sa-close]')) { this.close(); return; }
      if (event.target.closest('[data-sa-info-close]')) { this.closeDialog(this.info); return; }
      if (event.target.closest('[data-sa-info]')) { this.info.showModal(); return; }
      if (event.target.closest('[data-sa-delivery-close]')) { this.closeDialog(this.deliveryInfo); return; }
      if (event.target.closest('[data-sa-delivery]')) { await this.openDelivery(); return; }
      if (event.target.closest('[data-sa-retry]')) { this.store.refresh().catch(() => {}); return; }
      if (event.target.closest('[data-sa-undo]')) { await this.undoRemove(); return; }
      if (event.target.closest('[data-sa-dock-toggle]')) { this.toggleDock(); return; }
      const control = event.target.closest('[data-sa-mutates]');
      if (!control || control.matches('select, input') || this.store.mutating || this.navigating || this.profileUpdating) return;
      if (control.dataset.saAdd && this.dock?.contains(control) && Date.now() - (this.dockMovedAt || 0) < 500) return;
      if (control.hasAttribute('data-sa-profile-update')) { this.clearError(); await this.updateMeasurement(control.dataset.key); return; }
      control.dataset.saLoading = 'true';
      this.clearError();
      try {
        if (control.dataset.saCheckout) {
          const protectedCheckout = control.dataset.saCheckout === 'protected';
          const expected = this.store.cart.total_price;
          this.navigating = true;
          await this.store.checkout(protectedCheckout);
          if (this.store.unconfirmed) { this.navigating = false; this.setBusy(false); return; }
          if (this.store.cart.total_price !== expected) { this.navigating = false; this.setBusy(false); this.showError(this.strings.price_changed); this.broadcast(); return; }
          this.broadcast();
          window.location.assign(`${this.config.root}checkout`);
          return;
        }
        if (control.hasAttribute('data-sa-protection')) await this.store.setProtection(control.getAttribute('aria-checked') !== 'true');
        else if (control.dataset.saAdd) await this.store.add(control.dataset.saAdd, 1, {'_Added From': 'Cart drawer recommendation'});
        else {
          const item = this.store.cart?.items.find(item => item.key === control.dataset.key);
          if (!item) return;
          const action = control.dataset.saAction;
          if (!['remove', 'increase', 'decrease'].includes(action)) return;
          const removed = action === 'remove' ? {
            id: item.variant_id,
            quantity: item.quantity,
            title: this.cartTitle(item).name,
            properties: Object.fromEntries(Object.entries(item.properties || {}).filter(([, value]) => value !== null && value !== '')),
            // Removing the last item also takes Checkout+ out (nothing left to protect); Undo puts it back (QA 2026-10-20).
            protection: this.store.cart.items.some(line => isCoverage(line, this.config)),
          } : null;
          await this.store.setQuantity(item.key, action === 'remove' ? 0 : item.quantity + (action === 'increase' ? 1 : -1));
          if (removed) this.showUndo(removed);
        }
        this.broadcast();
      } catch { this.navigating = false; this.setBusy(this.store.pending > 0); }
    }
  }
  customElements.define('space-armory-cart', SpaceArmoryCart);
})();
